"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import dynamic from "next/dynamic";
import { useAuth } from "@/lib/auth";
import { SIGNATURE_FONTS, FONT_LOAD_URL_PRIMARY, FONT_LOAD_URL_DEFERRED, SIGNATURE_COLORS } from "@/lib/signatureFonts";

const DrawPad = dynamic(() => import("@/components/signing/DrawPad"), { ssr: false });
const UploadSignature = dynamic(() => import("@/components/signing/UploadSignature"), { ssr: false });

type Tab = "type" | "draw" | "upload";

export default function SignatureGeneratorTool() {
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>("type");
  const [name, setName] = useState("");
  const [selectedFont, setSelectedFont] = useState(0);
  const [selectedColor, setSelectedColor] = useState(0);
  const [fontSize, setFontSize] = useState(64);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // On mount: if user just came back from login/register with a pending signature, auto-save it
  const pendingSaveAttempted = useRef(false);
  useEffect(() => {
    if (pendingSaveAttempted.current || !isAuthenticated) return;
    const pending = typeof window !== "undefined" ? localStorage.getItem("sf_pending_signature") : null;
    if (!pending) return;
    pendingSaveAttempted.current = true;
    try {
      const { image, sigName } = JSON.parse(pending);
      if (!image) return;
      localStorage.removeItem("sf_pending_signature");
      // Auto-save
      const accessToken = localStorage.getItem("sf_access_token");
      if (!accessToken) return;
      setSaving(true);
      fetch("/api/auth/me/signatures", {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ name: sigName || "My signature", image_base64: image }),
      }).then((res) => {
        if (res.ok) {
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 3000);
        }
      }).catch(() => {}).finally(() => setSaving(false));
    } catch (_e) {
      localStorage.removeItem("sf_pending_signature");
    }
  }, [isAuthenticated]);

  // Load Google Fonts — non-render-blocking via media="print" trick
  useEffect(() => {
    // Preconnect to Google Fonts domains for faster resolution
    for (const origin of ["https://fonts.googleapis.com", "https://fonts.gstatic.com"]) {
      if (!document.querySelector(`link[rel="preconnect"][href="${origin}"]`)) {
        const pc = document.createElement("link");
        pc.rel = "preconnect";
        pc.href = origin;
        pc.crossOrigin = "anonymous";
        document.head.appendChild(pc);
      }
    }

    // Helper: load stylesheet without blocking render
    const loadFont = (url: string) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = url;
      link.media = "print"; // Non-blocking: browser fetches but doesn't apply
      link.onload = () => { link.media = "all"; }; // Apply once loaded
      document.head.appendChild(link);
    };

    // Primary fonts (above-fold, first 4) — load immediately but non-blocking
    loadFont(FONT_LOAD_URL_PRIMARY);

    // Secondary fonts (below-fold, remaining 8) — defer 1s after paint
    const deferTimer = setTimeout(() => loadFont(FONT_LOAD_URL_DEFERRED), 1000);

    const timeout = setTimeout(() => setFontsLoaded(true), 2000);
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => setFontsLoaded(true));
    }
    return () => { clearTimeout(deferTimer); clearTimeout(timeout); };
  }, []);

  // Render typed signature on canvas
  useEffect(() => {
    if (activeTab !== "type") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;

    const dpr = window.devicePixelRatio || 1;
    const displayWidth = canvas.clientWidth;
    const displayHeight = canvas.clientHeight;
    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr;
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, displayWidth, displayHeight);

    if (!name.trim()) {
      ctx.fillStyle = "#94a3b8";
      ctx.font = "16px system-ui";
      ctx.textAlign = "center";
      ctx.fillText("Your signature will appear here", displayWidth / 2, displayHeight / 2 + 6);
      setSignatureData(null);
      return;
    }

    ctx.fillStyle = SIGNATURE_COLORS[selectedColor].value;
    ctx.font = `${fontSize}px ${SIGNATURE_FONTS[selectedFont].family}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(name, displayWidth / 2, displayHeight / 2);

    // Generate tight-fit PNG for download/save
    const metrics = ctx.measureText(name);
    const textWidth = metrics.width;
    const textHeight = fontSize * 1.4;
    const padding = 10;
    const dlCanvas = document.createElement("canvas");
    dlCanvas.width = (textWidth + padding * 2) * dpr;
    dlCanvas.height = (textHeight + padding * 2) * dpr;
    const dlCtx = dlCanvas.getContext("2d")!;
    dlCtx.scale(dpr, dpr);
    dlCtx.clearRect(0, 0, dlCanvas.width, dlCanvas.height);
    dlCtx.fillStyle = SIGNATURE_COLORS[selectedColor].value;
    dlCtx.font = `${fontSize}px ${SIGNATURE_FONTS[selectedFont].family}`;
    dlCtx.textAlign = "center";
    dlCtx.textBaseline = "middle";
    dlCtx.fillText(name, textWidth / 2 + padding, textHeight / 2 + padding);
    setSignatureData(dlCanvas.toDataURL("image/png"));
  }, [activeTab, name, selectedFont, selectedColor, fontSize, fontsLoaded]);

  const handleDrawChange = useCallback((dataUrl: string | null) => {
    setSignatureData(dataUrl);
  }, []);

  const handleUploadChange = useCallback((dataUrl: string | null) => {
    setSignatureData(dataUrl);
  }, []);

  const download = useCallback(() => {
    if (!signatureData) return;
    const a = document.createElement("a");
    a.href = signatureData;
    a.download = "signature.png";
    a.click();
    window.sfAnalytics?.track("tool_run", "Tool", "signature-generator");
  }, [signatureData]);

  const saveSignature = useCallback(async () => {
    if (!signatureData) return;
    const accessToken = typeof window !== "undefined" ? localStorage.getItem("sf_access_token") : null;
    if (!accessToken || !isAuthenticated) {
      // Stash the signature so it auto-saves after login/register
      localStorage.setItem("sf_pending_signature", JSON.stringify({
        image: signatureData,
        sigName: name.trim() || "My signature",
      }));
      window.location.href = `/register?redirect=/tools/signature-generator`;
      return;
    }
    setSaving(true);
    setSaveSuccess(false);
    try {
      const res = await fetch("/api/auth/me/signatures", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: name.trim() || "My signature", image_base64: signatureData }),
      });
      if (res.status === 401) {
        // Token expired — stash signature and redirect to login
        localStorage.setItem("sf_pending_signature", JSON.stringify({
          image: signatureData,
          sigName: name.trim() || "My signature",
        }));
        window.location.href = `/login?redirect=/tools/signature-generator`;
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || "Failed to save signature");
        return;
      }
      setSaveSuccess(true);
      window.sfAnalytics?.track("signature_saved", "Signature");
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (_e) {
      alert("Failed to save signature");
    } finally {
      setSaving(false);
    }
  }, [signatureData, name, isAuthenticated]);

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        {([
          { key: "type" as Tab, label: "Type", icon: "M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" },
          { key: "draw" as Tab, label: "Draw", icon: "M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" },
          { key: "upload" as Tab, label: "Upload", icon: "M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" },
        ]).map((tab) => (
          <button
            key={tab.key}
            onClick={() => { setActiveTab(tab.key); setSignatureData(null); }}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? "border-[var(--color-gold)] text-[var(--color-ink)]"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <span className="flex items-center gap-1.5">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
              </svg>
              {tab.label}
            </span>
          </button>
        ))}
      </div>

      {/* Type tab */}
      {activeTab === "type" && (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Your name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Smith"
              className="input-field"
              maxLength={60}
            />
          </div>

          {/* Preview */}
          <div className="rounded-xl border border-slate-200 overflow-hidden" style={{ background: "repeating-conic-gradient(#f1f5f9 0% 25%, white 0% 50%) 50% / 16px 16px" }}>
            <canvas ref={canvasRef} className="w-full" style={{ height: 160 }} />
          </div>

          {/* Font grid */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Font style</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {SIGNATURE_FONTS.map((font, i) => (
                <button
                  key={font.name}
                  onClick={() => setSelectedFont(i)}
                  className={`rounded-lg border-2 px-3 py-3 text-left transition-all duration-150 ${
                    i === selectedFont
                      ? "border-[var(--color-gold)] bg-[var(--color-gold-muted)]/30"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <span className="text-xl text-slate-800 block whitespace-nowrap py-1" style={{ fontFamily: font.family, lineHeight: 2.2 }}>
                    {name.trim() || "Signature"}
                  </span>
                  <span className="text-[10px] text-slate-400 mt-0.5 block">{font.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Color + Size */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Color</label>
              <div className="flex gap-2">
                {SIGNATURE_COLORS.map((color, i) => (
                  <button
                    key={color.name}
                    onClick={() => setSelectedColor(i)}
                    className={`h-9 w-9 rounded-full border-2 transition-all ${
                      i === selectedColor ? "border-[var(--color-gold)] scale-110 shadow-md" : "border-slate-200"
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Size: {fontSize}px
              </label>
              <input
                type="range"
                min={32}
                max={96}
                value={fontSize}
                onChange={(e) => setFontSize(Number(e.target.value))}
                className="w-full accent-[var(--color-gold)]"
              />
            </div>
          </div>
        </div>
      )}

      {/* Draw tab */}
      {activeTab === "draw" && (
        <div className="min-h-[280px]">
          <DrawPad onSignatureChange={handleDrawChange} />
        </div>
      )}

      {/* Upload tab */}
      {activeTab === "upload" && (
        <div className="min-h-[280px]">
          <UploadSignature onSignatureChange={handleUploadChange} />
        </div>
      )}

      {/* Preview area for draw/upload */}
      {activeTab !== "type" && signatureData && (
        <div className="rounded-xl border border-slate-200 p-4 flex items-center justify-center" style={{ background: "repeating-conic-gradient(#f1f5f9 0% 25%, white 0% 50%) 50% / 16px 16px", minHeight: 120 }}>
          <img src={signatureData} alt="Signature preview" className="max-h-24 max-w-full object-contain" />
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={download}
          disabled={!signatureData}
          className="btn-primary flex-1 py-3"
        >
          <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
          Download as PNG
        </button>
        <button
          onClick={saveSignature}
          disabled={!signatureData || saving}
          className="btn-secondary flex-1 py-3"
        >
          {saving ? (
            "Saving..."
          ) : saveSuccess ? (
            <span className="flex items-center justify-center gap-1.5 text-emerald-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Saved!
            </span>
          ) : isAuthenticated ? (
            <>
              <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
              </svg>
              Save for future use
            </>
          ) : (
            <>
              <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
              </svg>
              Sign in to save
            </>
          )}
        </button>
      </div>
    </div>
  );
}
